<template>
  <div class="template-variable-shortcuts">
    <h2 v-if="title" class="tvs-title">{{ title }}</h2>
    <slot name="hint">
      <p v-if="hint" class="tvs-hint">{{ hint }}</p>
    </slot>

    <div v-if="showTags && normalizedKeys.length > 0" class="tvs-tags">
      <span
        v-for="v in normalizedKeys"
        :key="v"
        class="tvs-tag"
      >
        {{ wrapVar(v) }}
        <i class="ri-close-line" @click.stop="removeKey(v)" />
      </span>
    </div>

    <div class="tvs-shortcuts">
      <span v-if="shortcutsLabel != null && shortcutsLabel !== ''" class="tvs-shortcuts-label">{{ shortcutsLabel }}</span>
      <button
        v-for="item in items"
        :key="item.key"
        type="button"
        class="tvs-btn"
        :class="{ active: normalizedKeys.includes(item.key), disabled: disabled }"
        :disabled="disabled"
        @click="onShortcutClick(item.key)"
      >
        <span class="tvs-btn-tag">{{ wrapVar(item.key) }}</span>
        <small class="tvs-btn-label">{{ item.label }}</small>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  /** @type {{ key: string, label: string }[]} */
  items: {
    type: Array,
    required: true
  },
  /** 已宣告的變數鍵名 */
  modelValue: {
    type: Array,
    default: () => []
  },
  /**
   * insert：每次點擊 emit insert，並在未宣告時加入宣告
   * toggle：已宣告則移除宣告不 emit；未宣告則加入並 emit insert
   */
  behavior: {
    type: String,
    default: 'insert',
    validator: (v) => v === 'insert' || v === 'toggle'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  showTags: {
    type: Boolean,
    default: true
  },
  title: {
    type: String,
    default: ''
  },
  hint: {
    type: String,
    default: ''
  },
  shortcutsLabel: {
    type: String,
    default: '快速插入：'
  }
});

const emit = defineEmits(['update:modelValue', 'insert']);

/** API 或舊資料可能傳 null／非陣列，避免 .includes / .filter 拋錯 */
const normalizedKeys = computed(() =>
  Array.isArray(props.modelValue) ? props.modelValue : []
);

function wrapVar(key) {
  return `{{${key}}}`;
}

function removeKey(key) {
  const next = normalizedKeys.value.filter((k) => k !== key);
  emit('update:modelValue', next);
}

function onShortcutClick(key) {
  if (props.disabled) return;

  const declared = [...normalizedKeys.value];
  const idx = declared.indexOf(key);

  if (props.behavior === 'toggle') {
    if (idx >= 0) {
      declared.splice(idx, 1);
      emit('update:modelValue', declared);
      return;
    }
    declared.push(key);
    emit('update:modelValue', declared);
    emit('insert', key);
    return;
  }

  emit('insert', key);
  if (idx < 0) {
    declared.push(key);
    emit('update:modelValue', declared);
  }
}
</script>

<style scoped>
.template-variable-shortcuts {
  width: 100%;
}

.tvs-title {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px;
}

.tvs-hint {
  font-size: 13px;
  color: #6b7280;
  margin: -8px 0 12px;
  line-height: 1.5;
}

.tvs-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
  min-height: 24px;
}

.tvs-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eff6ff;
  color: #3b82f6;
  border: 1px solid #bfdbfe;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 13px;
  font-family: ui-monospace, monospace;
}

.tvs-tag i {
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}

.tvs-tag i:hover {
  color: #1d4ed8;
}

.tvs-shortcuts {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.tvs-shortcuts-label {
  font-size: 12px;
  color: #9ca3af;
  margin-right: 2px;
}

.tvs-btn {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 4px 10px;
  font-size: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition:
    border-color 0.15s,
    background 0.15s,
    color 0.15s;
}

.tvs-btn:hover:not(:disabled) {
  border-color: #93c5fd;
  background: #f8fafc;
}

.tvs-btn.active {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #1d4ed8;
}

.tvs-btn:disabled,
.tvs-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tvs-btn-tag {
  font-family: ui-monospace, monospace;
  font-weight: 500;
}

.tvs-btn-label {
  font-size: 10px;
  color: #9ca3af;
  text-align: center;
  max-width: 140px;
  line-height: 1.25;
}

.tvs-btn.active .tvs-btn-label {
  color: #3b82f6;
}
</style>
